<template>
  <div
    class="ai-suggestion-box"
    @mouseenter="fnHoverEnter"
    @mouseleave="fnHoverLeave"
  >
    <div class="ai-suggestion-header">
      <span class="ai-label">AI</span>
      <span class="ai-confidence" v-if="data.confidence">
        {{ Math.round(data.confidence * 100) }}%
      </span>
      <span class="ai-intent" v-if="data.intent">{{ intentLabel }} <span class="ai-intent-code">{{ data.intent }}</span></span>
      <span class="ai-message-time" v-if="customerMessageTime">
        {{ customerMessageTime }}
        <span v-if="data.used && data.used.isUsed" class="ai-used-label">(ตอบแล้ว)</span>
      </span>
      <span
        v-if="hasJumpTarget"
        class="view-message-btn"
        @click.stop="fnJumpToMessage"
        title="ดูข้อความที่เกี่ยวข้อง"
      >
        <i class="fas fa-eye"></i> ดูข้อความลูกค้า
      </span>
      <div class="ai-feedback-actions">
        <span
          class="feedback-btn feedback-incorrect"
          :class="{
            active: data.feedback && data.feedback.isCorrect === false,
          }"
          @click.stop="fnFeedbackIncorrect"
          title="Incorrect"
        >
          <i class="fas fa-thumbs-down"></i>
        </span>
        <span
          class="feedback-btn close-btn"
          @click.stop="fnClose"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </span>
      </div>
    </div>
    <div class="ai-suggestion-content">
      <div class="suggestion-dropdown">
        <span
          class="suggestion-chip suggestion-primary"
          :title="data.suggestedReply"
          @click="fnUseSuggestion"
        >
          {{ data.suggestedReply }}
        </span>
        <div class="dropdown-menu" :class="{ 'has-add': hasMultipleSuggestions }">
          <div
            class="dropdown-item"
            @click="fnSendImmediately(data.suggestedReply)"
          >
            <i class="fas fa-paper-plane"></i> ส่งทันที
          </div>
          <div class="dropdown-item" @click="fnUseSuggestion">
            <i class="fas fa-edit"></i> แก้ไข
          </div>
          <div
            v-if="hasMultipleSuggestions"
            class="dropdown-item dropdown-add"
            @click="fnAppendSuggestion(data.suggestedReply)"
          >
            <i class="fas fa-plus"></i> เพิ่มข้อความนี้
          </div>
        </div>
      </div>
      <div
        v-for="(alt, index) in data.alternativeReplies"
        :key="index"
        class="suggestion-dropdown"
      >
        <span
          class="suggestion-chip suggestion-alt"
          :title="alt"
          @click="fnUseAlternative(alt)"
        >
          {{ alt }}
        </span>
        <div class="dropdown-menu" :class="{ 'has-add': hasMultipleSuggestions }">
          <div class="dropdown-item" @click="fnSendAlternativeImmediately(alt)">
            <i class="fas fa-paper-plane"></i> ส่งทันที
          </div>
          <div class="dropdown-item" @click="fnUseAlternative(alt)">
            <i class="fas fa-edit"></i> แก้ไข
          </div>
          <div
            v-if="hasMultipleSuggestions"
            class="dropdown-item dropdown-add"
            @click="fnAppendSuggestion(alt)"
          >
            <i class="fas fa-plus"></i> เพิ่มข้อความนี้
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiSuggestion',
  props: ['data'],
  computed: {
    hasJumpTarget() {
      const ids =
        this.data.chatHistoryIds ||
        (this.data.chatHistoryId ? [this.data.chatHistoryId] : []);
      return ids.length > 0;
    },
    intentLabel() {
      const intent = this.data.intent || '';
      if (intent.startsWith('booking')) return 'การจองทัวร์';
      if (intent.startsWith('tour.search')) return 'ค้นหาทัวร์';
      if (intent.startsWith('tour.details')) return 'รายละเอียดโปรแกรม';
      if (intent.startsWith('document')) return 'เอกสาร/พาสปอร์ต/วีซ่า';
      if (intent.startsWith('payment')) return 'การชำระเงิน';
      if (intent.startsWith('price.inquiry')) return 'สอบถามราคา';
      if (intent.startsWith('promotion')) return 'โปรโมชั่น';
      if (intent.startsWith('food') || intent.startsWith('preference.food')) return 'ความต้องการอาหาร';
      if (intent.startsWith('flight')) return 'สายการบิน/กระเป๋า';
      if (intent.startsWith('handoff')) return 'ติดต่อพนักงาน';
      if (intent === 'greeting' || intent.startsWith('fallback') || intent.startsWith('chat')) return 'อื่นๆ';
      return 'อื่นๆ';
    },
    customerMessageTime() {
      if (!this.data.customerMessageAt) return '';
      const d = new Date(this.data.customerMessageAt);
      const day = String(d.getDate()).padStart(2, '0');
      const thaiMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      const month = thaiMonths[d.getMonth()];
      const buddhistYear = String(d.getFullYear() + 543).slice(-2);
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${day} ${month} ${buddhistYear} | ${hours}:${minutes} น.`;
    },
    hasMultipleSuggestions() {
      return (
        this.data.intent &&
        this.data.intent.startsWith('tour.search') &&
        this.data.alternativeReplies &&
        this.data.alternativeReplies.length > 0
      );
    },
  },
  methods: {
    fnUseSuggestion() {
      this.$emit('useSuggestion', {
        suggestionId: this.data.suggestionId,
        text: this.data.suggestedReply,
      });
    },
    fnUseAlternative(text) {
      this.$emit('useSuggestion', {
        suggestionId: this.data.suggestionId,
        text: text,
      });
    },
    fnAppendSuggestion(text) {
      this.$emit('appendSuggestion', {
        suggestionId: this.data.suggestionId,
        text,
      });
    },
    fnSendImmediately(text) {
      this.$emit('sendSuggestion', {
        suggestionId: this.data.suggestionId,
        text: text,
      });
    },
    fnSendAlternativeImmediately(text) {
      this.$emit('sendSuggestion', {
        suggestionId: this.data.suggestionId,
        text: text,
      });
    },
    fnFeedbackIncorrect() {
      if (this.data.feedback && this.data.feedback.isCorrect === false) return;
      this.$emit('feedback', {
        suggestionId: this.data.suggestionId,
        isCorrect: false,
      });
    },
    fnClose() {
      this.$emit('hover', []);
      this.$emit('close', this.data.suggestionId);
    },
    fnHoverEnter() {
      const ids =
        this.data.chatHistoryIds ||
        (this.data.chatHistoryId ? [this.data.chatHistoryId] : []);
      this.$emit('hover', ids);
    },
    fnHoverLeave() {
      this.$emit('hover', []);
    },
    fnJumpToMessage() {
      const ids =
        this.data.chatHistoryIds ||
        (this.data.chatHistoryId ? [this.data.chatHistoryId] : []);
      if (ids.length > 0) {
        this.$emit('jumpToMessage', ids[0]);
      }
    },
  },
};
</script>

<style scoped>
.ai-suggestion-box {
  background: linear-gradient(135deg, #e8f4fd 0%, #d4e9fa 100%);
  border: 1px solid #90caf9;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 8px;
  position: relative;
}

.ai-suggestion-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.ai-label {
  background: #1976d2;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 14px;
  text-transform: uppercase;
}

.ai-confidence {
  background: #43a047;
  color: white;
  font-size: 16px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 14px;
}

.ai-intent {
  color: #555;
  font-size: 17px;
  font-style: italic;
}

.ai-intent-code {
  color: #999;
  font-size: 14px;
  font-weight: normal;
  margin-left: 8px;
}

.view-message-btn {
  background: #ff9800;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.view-message-btn:hover {
  background: #f57c00;
}

.view-message-btn i {
  font-size: 11px;
}

.ai-message-time {
  color: #999;
  font-size: 14px;
  margin-left: 2px;
}

.ai-used-label {
  color: #43a047;
  font-weight: 600;
  margin-left: 4px;
}

.ai-feedback-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.feedback-btn {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 20px;
  color: #888;
  transition: all 0.2s;
}

.feedback-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.feedback-btn.feedback-incorrect:hover,
.feedback-btn.feedback-incorrect.active {
  color: #c62828;
  background: rgba(198, 40, 40, 0.15);
}

.feedback-btn.close-btn:hover {
  color: #666;
  background: rgba(0, 0, 0, 0.1);
}

.ai-suggestion-content {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

.suggestion-dropdown {
  position: relative;
  display: inline-block;
}

.suggestion-chip {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 10px 16px;
  border-radius: 18px;
  font-size: 18px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 400px;
  word-break: break-word;
}

.suggestion-primary {
  background: #1976d2;
  color: white;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(25, 118, 210, 0.3);
}

.suggestion-dropdown:hover .suggestion-primary {
  background: #1565c0;
  box-shadow: 0 3px 6px rgba(25, 118, 210, 0.4);
}

.suggestion-alt {
  background: white;
  color: #333;
  border: 1px solid #bbdefb;
}

.suggestion-dropdown:hover .suggestion-alt {
  background: #e3f2fd;
  border-color: #1976d2;
}

.dropdown-menu {
  position: absolute;
  top: -56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  display: none;
  white-space: nowrap;
}

.dropdown-menu.has-add {
  flex-wrap: wrap;
  width: max-content;
  max-width: 340px;
}

.suggestion-dropdown:hover > .dropdown-menu {
  display: flex !important;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
  min-height: 42px;
  box-sizing: border-box;
  flex: 1;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item:first-child {
  border-right: 1px solid #eee;
  border-radius: 8px 0 0 8px;
  color: #1976d2;
}

.dropdown-item:not(:first-child):not(:last-child) {
  border-right: 1px solid #eee;
}

.dropdown-item:last-child {
  border-radius: 0 8px 8px 0;
}

.dropdown-item i {
  font-size: 15px;
}

.dropdown-item:nth-child(2) {
  color: #43a047;
}

.dropdown-item.dropdown-add {
  color: #ff9800;
  padding: 10px 16px;
}
</style>




// WEBPACK FOOTER //
// src/components/AiSuggestion.vue